import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Icon from '@mdi/react';
import { mdiDotsVertical, mdiDomain, mdiPhone, mdiAccount, mdiEye, mdiEyeOff } from '@mdi/js';
import { toast } from 'react-toastify';

import {
    selectActiveSubAccount,
    activeSubAccountSelector,
    changeView,
    getSubAccountDetails,
    reloadAccountsSelector,
    toggleAccountActivation,
    deleteSubAccount,
    subaccountChangePassword,
} from '../../features/subaccounts/subaccountSlice';

const useStyles = makeStyles(() => ({
    card: {
        borderRadius: 14,
        border: '1px solid rgba(148, 163, 184, 0.35)',
        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
    },
    cardContent: {
        padding: 14,
        '&:last-child': {
            paddingBottom: 14,
        },
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 10,
    },
    titleBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    nameRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
    },
    nameText: {
        fontWeight: 500,
        color: '#0f172a',
        fontSize: '0.98rem',
    },
    statusChip: {
        borderRadius: 999,
        fontWeight: 500,
        fontSize: '0.68rem',
    },
    statusActive: {
        backgroundColor: 'rgba(34, 197, 94, 0.16)',
        color: '#14532d',
        border: '1px solid rgba(34, 197, 94, 0.5)',
    },
    statusInactive: {
        backgroundColor: 'rgba(248, 113, 113, 0.18)',
        color: '#7f1d1d',
        border: '1px solid rgba(248, 113, 113, 0.6)',
    },
    metaGrid: {
        marginTop: 8,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 16px',
    },
    metaItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 0',
    },
    metaIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        color: '#64748b',
    },
    companyIcon: {
        color: '#4f46e5',
    },
    phoneIcon: {
        color: '#0ea5e9',
    },
    userIcon: {
        color: '#10b981',
    },
    metaValue: {
        fontSize: '0.82rem',
        color: '#1f2937',
        fontWeight: 400,
        lineHeight: 1.2,
    },
    menuButton: {
        borderRadius: 12,
        border: '1px solid rgba(148, 163, 184, 0.4)',
        backgroundColor: '#ffffff',
        padding: 6,
        boxShadow: '0 6px 12px rgba(15, 23, 42, 0.08)',
        '&:hover': {
            backgroundColor: '#f1f5f9',
        },
    },
    actionDialogPaper: {
        borderRadius: 16,
        background: 'linear-gradient(135deg, rgba(248,250,252,0.98), rgba(226,232,240,0.95))',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
        overflow: 'hidden',
    },
    actionDialogTitle: {
        padding: '20px 24px 16px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        color: '#f8fafc',
    },
    actionDialogContent: {
        padding: '20px 24px 24px',
    },
    section: {
        marginTop: 16,
        padding: 16,
        borderRadius: 14,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(148, 163, 184, 0.35)',
        boxShadow: '0 12px 20px rgba(15, 23, 42, 0.08)',
    },
    sectionTitle: {
        fontWeight: 700,
        color: '#0f172a',
        marginBottom: 12,
    },
    actionGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 10,
    },
    actionButton: {
        textTransform: 'none',
        justifyContent: 'space-between',
        borderRadius: 12,
        borderColor: 'rgba(148, 163, 184, 0.5)',
        color: '#0f172a',
        fontWeight: 600,
        backgroundColor: '#ffffff',
    },
    primaryButton: {
        background: 'linear-gradient(135deg, #38bdf8, #0ea5e9)',
        color: '#0f172a',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 12,
        boxShadow: '0 12px 20px rgba(14, 165, 233, 0.25)',
        '&:hover': {
            background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
        },
    },
    dangerButton: {
        borderColor: 'rgba(248, 113, 113, 0.7)',
        color: '#b91c1c',
        backgroundColor: 'rgba(254, 226, 226, 0.6)',
    },
    dangerFilledButton: {
        background: 'linear-gradient(135deg, #f87171, #ef4444)',
        color: '#ffffff',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 12,
        boxShadow: '0 12px 20px rgba(239, 68, 68, 0.25)',
        '&:hover': {
            background: 'linear-gradient(135deg, #ef4444, #f87171)',
        },
    },
    actionDialogActions: {
        padding: '14px 24px 20px',
        backgroundColor: 'rgba(248, 250, 252, 0.9)',
    },
    confirmDialogTitle: {
        padding: '18px 24px 12px',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',
        color: '#f8fafc',
    },
    confirmDialogContent: {
        padding: '12px 24px 20px',
    },
    confirmInfo: {
        marginTop: 10,
        padding: '10px 12px',
        borderRadius: 12,
        backgroundColor: '#ffffff',
        border: '1px solid rgba(148, 163, 184, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    field: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: 12,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(148, 163, 184, 0.4)',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#38bdf8',
            borderWidth: 2,
        },
    },
}));

const initialPasswordVisibility = {
    password: false,
    confirmPassword: false,
};

const isSubAccountActive = (active) => active === true || active === 1 || active === '1';

const SubAccountListItem = ({ subaccount }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const activeSubAccount = useSelector(activeSubAccountSelector);
    const reloadAccounts = useSelector(reloadAccountsSelector);

    const [localSubAccount, setLocalSubAccount] = useState({ ...subaccount });

    const activeHasDetails = Boolean(
        activeSubAccount &&
            (Object.prototype.hasOwnProperty.call(activeSubAccount, 'assets') ||
                Object.prototype.hasOwnProperty.call(activeSubAccount, 'zones') ||
                Object.prototype.hasOwnProperty.call(activeSubAccount, 'markers') ||
                Object.prototype.hasOwnProperty.call(activeSubAccount, 'reports'))
    );
    useEffect(() => {
        if (
            activeSubAccount &&
            activeSubAccount.subuser_id &&
            subaccount &&
            activeSubAccount.subuser_id === subaccount.subuser_id
        ) {
            if (reloadAccounts || !activeHasDetails) {
                dispatch(getSubAccountDetails({ subuser_id: activeSubAccount.subuser_id }));
            }
        }
        if (activeSubAccount && subaccount && activeSubAccount.subuser_id === subaccount.subuser_id) {
            setLocalSubAccount({ ...activeSubAccount });
        }
    }, [dispatch, activeSubAccount, reloadAccounts, subaccount, activeHasDetails]);
    useEffect(() => {
        if (!activeSubAccount || activeSubAccount.subuser_id !== subaccount.subuser_id) {
            setLocalSubAccount({ ...subaccount });
        }
    }, [activeSubAccount, subaccount]);

    const handleVehicleSelect = () => {
        dispatch(selectActiveSubAccount(localSubAccount));
        dispatch(changeView('vehicleSelect'));
        setActionPanelOpen(false);
    };

    const handleZoneSelect = () => {
        dispatch(selectActiveSubAccount(localSubAccount));
        dispatch(changeView('zoneSelect'));
        setActionPanelOpen(false);
    };

    const handleMarkerSelect = () => {
        dispatch(selectActiveSubAccount(localSubAccount));
        dispatch(changeView('markerSelect'));
        setActionPanelOpen(false);
    };

    const handleReportSelect = () => {
        dispatch(selectActiveSubAccount(localSubAccount));
        dispatch(changeView('reportSelect'));
        setActionPanelOpen(false);
    };

    const handleDeleteUser = () => {
        dispatch(deleteSubAccount({ subuser_id: localSubAccount.subuser_id }));
        setOpenDelete(false);
    };

    const [actionPanelOpen, setActionPanelOpen] = useState(false);
    const [openPassword, setOpenPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordVisibility, setPasswordVisibility] = useState(initialPasswordVisibility);
    const [isPasswordChanging, setIsPasswordChanging] = useState(false);

    const [openDeactivate, setOpenDeactivate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [isActivationChanging, setIsActivationChanging] = useState(false);

    const handleClosePassword = () => {
        if (isPasswordChanging) {
            return;
        }

        setPassword('');
        setConfirmPassword('');
        setPasswordVisibility(initialPasswordVisibility);
        setOpenPassword(false);
    };

    const handleChangePassword = async () => {
        if (isPasswordChanging) {
            return;
        }

        setIsPasswordChanging(true);
        try {
            await dispatch(subaccountChangePassword({
                subuser_id: localSubAccount.subuser_id,
                password,
                confirmPassword,
            })).unwrap();
            toast.success('Sub account password changed successfully.');
            setPassword('');
            setConfirmPassword('');
            setPasswordVisibility(initialPasswordVisibility);
            setOpenPassword(false);
        } catch (error) {
            const message = typeof error === 'string'
                ? error
                : error?.error || error?.message || 'Unable to change the sub account password.';
            toast.error(message);
        } finally {
            setIsPasswordChanging(false);
        }
    };

    const openPasswordDialog = () => {
        setActionPanelOpen(false);
        setOpenPassword(true);
    };

    const openDeactivateDialog = () => {
        setActionPanelOpen(false);
        setOpenDeactivate(true);
    };

    const openDeleteDialog = () => {
        setActionPanelOpen(false);
        setOpenDelete(true);
    };

    const isActive = isSubAccountActive(localSubAccount.active);
    const passwordTooShort = password.length > 0 && password.length < 6;
    const passwordsDoNotMatch = confirmPassword.length > 0 && password !== confirmPassword;

    const toggleActivation = async () => {
        if (isActivationChanging) {
            return;
        }

        setIsActivationChanging(true);
        try {
            await dispatch(
                toggleAccountActivation({
                    subuser_id: localSubAccount.subuser_id,
                    active: !isActive,
                })
            ).unwrap();
            setOpenDeactivate(false);
        } catch (error) {
            const message = typeof error === 'string'
                ? error
                : error?.error || error?.message || 'Unable to update sub account status.';
            toast.error(message);
        } finally {
            setIsActivationChanging(false);
        }
    };

    const togglePasswordVisibility = (field) => () => {
        setPasswordVisibility((current) => ({ ...current, [field]: !current[field] }));
    };

    const passwordVisibilityAdornment = (field, label) => (
        <InputAdornment position="end">
            <IconButton
                aria-label={passwordVisibility[field] ? `Hide ${label}` : `Show ${label}`}
                edge="end"
                onClick={togglePasswordVisibility(field)}
                size="small"
            >
                <Icon path={passwordVisibility[field] ? mdiEyeOff : mdiEye} size={0.8} />
            </IconButton>
        </InputAdornment>
    );

    return (
        <div style={{ marginBottom: 10 }}>
            <Card
                className={classes.card}
                style={{
                    backgroundColor: isActive ? '#ffffff' : 'rgba(248, 250, 252, 0.8)',
                }}
            >
                <CardContent className={classes.cardContent}>
                    <Box className={classes.headerRow}>
                        <Box className={classes.titleBlock}>
                            <Box className={classes.nameRow}>
                                <Typography variant="subtitle1" className={classes.nameText}>
                                    {localSubAccount.person_name}
                                </Typography>
                                <Chip
                                    label={isActive ? 'Active' : 'Deactivated'}
                                    size="small"
                                    className={`${classes.statusChip} ${
                                        isActive ? classes.statusActive : classes.statusInactive
                                    }`}
                                    variant="outlined"
                                />
                            </Box>
                        </Box>
                        <IconButton
                            className={classes.menuButton}
                            onClick={() => setActionPanelOpen(true)}
                            size="small"
                        >
                            <Icon path={mdiDotsVertical} size={0.8} color="#334155" />
                        </IconButton>
                    </Box>
                    <Box className={classes.metaGrid}>
                        <Box className={classes.metaItem}>
                            <Box className={`${classes.metaIcon} ${classes.companyIcon}`}>
                                <Icon path={mdiDomain} size={0.7} color="currentColor" />
                            </Box>
                            <Typography className={classes.metaValue}>
                                {localSubAccount.company || '—'}
                            </Typography>
                        </Box>
                        <Box className={classes.metaItem}>
                            <Box className={`${classes.metaIcon} ${classes.phoneIcon}`}>
                                <Icon path={mdiPhone} size={0.7} color="currentColor" />
                            </Box>
                            <Typography className={classes.metaValue}>
                                {localSubAccount.phone_number || '—'}
                            </Typography>
                        </Box>
                        <Box className={classes.metaItem}>
                            <Box className={`${classes.metaIcon} ${classes.userIcon}`}>
                                <Icon path={mdiAccount} size={0.7} color="currentColor" />
                            </Box>
                            <Typography className={classes.metaValue}>
                                {localSubAccount.username || '—'}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <Dialog
                open={actionPanelOpen}
                onClose={() => setActionPanelOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ className: classes.actionDialogPaper }}
            >
                <DialogTitle className={classes.actionDialogTitle}>
                    <Typography variant="h6">Sub Account Actions</Typography>
                    <Typography variant="body2" style={{ opacity: 0.8 }}>
                        {localSubAccount.person_name} · {localSubAccount.username}
                    </Typography>
                </DialogTitle>
                <DialogContent className={classes.actionDialogContent}>
                    {isActive ? (
                        <>
                            <Box className={classes.section}>
                                <Typography className={classes.sectionTitle}>Selections</Typography>
                                <Box className={classes.actionGrid}>
                                    <Button
                                        variant="outlined"
                                        className={classes.actionButton}
                                        onClick={handleVehicleSelect}
                                    >
                                        Vehicle Selection
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        className={classes.actionButton}
                                        onClick={handleZoneSelect}
                                    >
                                        Zone Selection
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        className={classes.actionButton}
                                        onClick={handleMarkerSelect}
                                    >
                                        Marker Selection
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        className={classes.actionButton}
                                        onClick={handleReportSelect}
                                    >
                                        Report Selection
                                    </Button>
                                </Box>
                            </Box>
                            <Box className={classes.section}>
                                <Typography className={classes.sectionTitle}>Account Actions</Typography>
                                <Box className={classes.actionGrid}>
                                    <Button
                                        variant="outlined"
                                        className={classes.actionButton}
                                        onClick={openPasswordDialog}
                                    >
                                        Change Password
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        className={`${classes.actionButton} ${classes.dangerButton}`}
                                        onClick={openDeactivateDialog}
                                    >
                                        Deactivate
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Box className={classes.section}>
                            <Typography className={classes.sectionTitle}>Account Actions</Typography>
                            <Box className={classes.actionGrid}>
                                <Button
                                    variant="contained"
                                    className={classes.primaryButton}
                                    onClick={openDeactivateDialog}
                                >
                                    Reactivate
                                </Button>
                                <Button
                                    variant="outlined"
                                    className={`${classes.actionButton} ${classes.dangerButton}`}
                                    onClick={openDeleteDialog}
                                >
                                    Delete
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions className={classes.actionDialogActions}>
                    <Button onClick={() => setActionPanelOpen(false)} color="inherit">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openPassword}
                onClose={handleClosePassword}
                fullWidth
                maxWidth="xs"
                PaperProps={{ className: classes.actionDialogPaper }}
            >
                <DialogTitle className={classes.confirmDialogTitle}>
                    Change Password
                </DialogTitle>
                <DialogContent className={classes.confirmDialogContent}>
                    <DialogContentText>
                        <Box className={classes.confirmInfo}>
                            <Typography variant="subtitle2" style={{ color: '#0f172a' }}>
                                {localSubAccount.person_name}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#64748b' }}>
                                {localSubAccount.username}
                            </Typography>
                        </Box>
                    </DialogContentText>
                    <Box style={{ marginTop: 16 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="New Password"
                            type={passwordVisibility.password ? 'text' : 'password'}
                            variant="outlined"
                            className={classes.field}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={passwordTooShort}
                            helperText={passwordTooShort ? 'Password must be at least 6 characters.' : 'Use at least 6 characters.'}
                            autoComplete="new-password"
                            InputProps={{
                                endAdornment: passwordVisibilityAdornment('password', 'new password'),
                            }}
                            disabled={isPasswordChanging}
                        />
                    </Box>
                    <Box style={{ marginTop: 12 }}>
                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            type={passwordVisibility.confirmPassword ? 'text' : 'password'}
                            variant="outlined"
                            className={classes.field}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={passwordsDoNotMatch}
                            helperText={passwordsDoNotMatch ? 'Passwords must match.' : 'Enter the same password again.'}
                            autoComplete="new-password"
                            InputProps={{
                                endAdornment: passwordVisibilityAdornment('confirmPassword', 'confirm new password'),
                            }}
                            disabled={isPasswordChanging}
                        />
                    </Box>
                </DialogContent>
                <DialogActions className={classes.actionDialogActions}>
                    <Button onClick={handleClosePassword} disabled={isPasswordChanging}>Cancel</Button>
                    <Button
                        onClick={handleChangePassword}
                        disabled={isPasswordChanging || password.length < 6 || password !== confirmPassword}
                        className={classes.primaryButton}
                        variant="contained"
                    >
                        {isPasswordChanging ? 'Changing...' : 'Change Password'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDeactivate}
                onClose={() => !isActivationChanging && setOpenDeactivate(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ className: classes.actionDialogPaper }}
            >
                <DialogTitle className={classes.confirmDialogTitle}>
                    Sub Account {isActive ? 'Deactivation' : 'Activation'}
                </DialogTitle>
                <DialogContent className={classes.confirmDialogContent}>
                    <DialogContentText>
                        <Box className={classes.confirmInfo}>
                            <Typography variant="subtitle2" style={{ color: '#0f172a' }}>
                                {localSubAccount.person_name}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#64748b' }}>
                                {localSubAccount.username}
                            </Typography>
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions className={classes.actionDialogActions}>
                    <Button onClick={() => setOpenDeactivate(false)} disabled={isActivationChanging}>Cancel</Button>
                    <Button
                        onClick={toggleActivation}
                        disabled={isActivationChanging}
                        variant="contained"
                        className={isActive ? classes.dangerFilledButton : classes.primaryButton}
                    >
                        {isActivationChanging ? 'Updating...' : isActive ? 'Deactivate' : 'Reactivate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDelete}
                onClose={() => setOpenDelete(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ className: classes.actionDialogPaper }}
            >
                <DialogTitle className={classes.confirmDialogTitle}>
                    Sub Account Deletion
                </DialogTitle>
                <DialogContent className={classes.confirmDialogContent}>
                    <DialogContentText>
                        <Box className={classes.confirmInfo}>
                            <Typography variant="subtitle2" style={{ color: '#0f172a' }}>
                                {localSubAccount.person_name}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#64748b' }}>
                                {localSubAccount.username}
                            </Typography>
                        </Box>
                    </DialogContentText>
                    <Divider style={{ marginTop: 16 }} />
                    <Typography variant="body2" style={{ marginTop: 12, color: '#991b1b' }}>
                        This action permanently deletes the sub account.
                    </Typography>
                </DialogContent>
                <DialogActions className={classes.actionDialogActions}>
                    <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
                    <Button
                        onClick={handleDeleteUser}
                        variant="contained"
                        className={classes.dangerFilledButton}
                    >
                        Delete Sub Account
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default SubAccountListItem;
